import { useCallback, useEffect, useRef, useState } from "react";

const LAUNCH_BUSY_MS = 2000;

/**
 * Tracks the brief "Opening…" state after a wallet deep-link tap, and clears
 * it when the page loses focus (wallet likely opened) or after a short delay.
 */
const useDeepLinkFallback = (busyMs = LAUNCH_BUSY_MS) => {
  const [isLaunching, setIsLaunching] = useState(false);
  const timerRef = useRef(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setIsLaunching(false);
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") clearTimer();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", clearTimer);
    window.addEventListener("blur", clearTimer);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", clearTimer);
      window.removeEventListener("blur", clearTimer);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [clearTimer]);

  const startLaunch = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setIsLaunching(true);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setIsLaunching(false);
    }, busyMs);
  }, [busyMs]);

  return { startLaunch, isLaunching };
};

export default useDeepLinkFallback;
